import { Stepper as MantineStepper, StepperProps, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { getContrastingTextColor } from '../../../utils/color';
import classes from './Stepper.module.css';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedStepperProps extends StepperProps {
  gradientVariant?: GradientVariant;
}

interface StepperComponent extends React.ForwardRefExoticComponent<ThemedStepperProps & React.RefAttributes<HTMLDivElement>> {
  Step: typeof MantineStepper.Step;
  Completed: typeof MantineStepper.Completed;
}

const join = (...values: (string | undefined)[]) => values.filter(Boolean).join(' ') || undefined;

const Stepper = forwardRef<HTMLDivElement, ThemedStepperProps>(
  ({ gradientVariant = 'primary', classNames, style, children, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];

    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    // Mantine allows classNames to be a function; only the object form can be merged
    // here, so a function is passed through untouched rather than half-applied.
    const callerClassNames = typeof classNames === 'function' ? undefined : classNames;

    return (
      <MantineStepper
        ref={ref}
        classNames={
          callerClassNames === undefined && typeof classNames === 'function'
            ? classNames
            : {
                ...callerClassNames,
                stepIcon: join(classes.stepIcon, callerClassNames?.stepIcon),
                separator: join(classes.separator, callerClassNames?.separator),
              }
        }
        // The gradient varies per instance, so it reaches the stylesheet as custom
        // properties. Caller `style` wins, so an override is still possible.
        style={
          {
            '--stepper-gradient': gradientBg,
            '--stepper-gradient-from': gradient.from,
            '--stepper-gradient-text': autoTextColor,
            '--stepper-separator-gradient': `linear-gradient(90deg, ${gradient.from}, ${gradient.to})`,
            ...(style as React.CSSProperties),
          } as React.CSSProperties
        }
        {...rest}
      >
        {children}
      </MantineStepper>
    );
  }
) as StepperComponent;

Stepper.displayName = 'Stepper';

// Attach subcomponents
Stepper.Step = MantineStepper.Step;
Stepper.Completed = MantineStepper.Completed;

export default Stepper;
